/**
 * GridPreview.tsx
 * Grid layout preview component with Trustoo-style Image First & In-card Carousel
 * Path: components/admin-tabs/reviews/display-settings/previews/GridPreview.tsx
 */

import React, { useState } from 'react';
import { Review, WidgetSettings } from '../../../../../types';
import { Star, ChevronLeft, ChevronRight, BadgeCheck, PlayCircle } from 'lucide-react';

interface GridPreviewProps {
  reviews: Review[];
  settings: Partial<WidgetSettings>;
}

export const GridPreview: React.FC<GridPreviewProps> = ({ reviews, settings }) => {
  // Determine grid columns based on settings
  const columns = settings.reviews_per_page === 3 ? 'grid-cols-1 md:grid-cols-3' :
                 settings.reviews_per_page === 4 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4' :
                 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';

  // Gap settings
  const gap = settings.card_gap ? `${settings.card_gap}px` : '16px';

  // --- NEW SORTING LOGIC START ---
  const sortedReviews = React.useMemo(() => {
    // Create a shallow copy to sort
    const items = [...reviews];
    
    if (settings.prioritize_media) {
      items.sort((a, b) => {
        const aHasMedia = a.media_urls && a.media_urls.length > 0;
        const bHasMedia = b.media_urls && b.media_urls.length > 0;
        
        if (aHasMedia && !bHasMedia) return -1;
        if (!aHasMedia && bHasMedia) return 1;
        return 0;
      });
    }
    return items;
  }, [reviews, settings.prioritize_media]);
  // --- NEW SORTING LOGIC END ---

  return (
    <div className={`grid ${columns}`} style={{ gap: gap }}>
      {sortedReviews.map(review => (
        <ReviewCard key={review.id} review={review} settings={settings} />
      ))}
    </div>
  );
};

/**
 * Individual Card Component
 * Handles the "Carousel" state for reviews with multiple images
 */
const ReviewCard: React.FC<{ review: Review; settings: Partial<WidgetSettings> }> = ({ review, settings }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const hasMedia = review.media_urls && review.media_urls.length > 0;
  const mediaList = hasMedia ? review.media_urls : [];
  
  // Navigation for in-card carousel
  const nextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMedia) {
      setCurrentMediaIndex((prev) => (prev + 1) % mediaList.length);
    }
  };

  const prevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMedia) {
      setCurrentMediaIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className="w-3.5 h-3.5"
            style={{
              fill: star <= rating ? (settings.star_color || '#fbbf24') : 'none',
              color: star <= rating ? (settings.star_color || '#fbbf24') : '#e5e7eb'
            }}
          />
        ))}
      </div>
    );
  };

  // Card container style
  const cardStyle = settings.card_style === 'shadow' 
    ? 'shadow-lg border-transparent' 
    : 'border border-gray-200';

  return (
    <div 
      className={`bg-white rounded-xl overflow-hidden flex flex-col h-full transition-transform duration-300 hover:-translate-y-1 ${cardStyle}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* --- MEDIA SECTION (Image First) --- */}
      {settings.show_media && hasMedia ? (
        <div className="relative w-full aspect-[3/4] bg-gray-100 group">
          
          {/* Main Image */}
          <img
            src={mediaList[currentMediaIndex].url}
            alt={`Review by ${review.reviewer_name}`}
            className="w-full h-full object-cover transition-opacity duration-300"
          />

          {/* Video Indicator */}
          {mediaList[currentMediaIndex].type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 pointer-events-none">
              <PlayCircle className="w-12 h-12 text-white opacity-80" />
            </div>
          )}

          {/* Multiple Images Carousel Controls */}
          {mediaList.length > 1 && (
            <>
              {/* Left Arrow */}
              <button
                onClick={prevMedia}
                className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 rounded-full shadow-sm transition-opacity duration-200 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Right Arrow */}
              <button
                onClick={nextMedia}
                className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 rounded-full shadow-sm transition-opacity duration-200 ${
                  isHovered ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                {mediaList.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full shadow-sm transition-colors ${
                      idx === currentMediaIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Count Badge (Alternative to dots if many images) */}
          {mediaList.length > 5 && (
            <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-full backdrop-blur-sm">
              {currentMediaIndex + 1}/{mediaList.length}
            </div>
          )}
        </div>
      ) : (
        // Fallback for no media (Gradient Placeholder)
        <div className="w-full aspect-video bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border-b border-gray-100">
          <span className="text-4xl">💬</span>
        </div>
      )}

      {/* --- CONTENT SECTION --- */}
      <div className="p-4 flex flex-col flex-grow">
        
        {/* Rating & Date Row */}
        <div className="flex items-center justify-between mb-2">
          {renderStars(review.rating)}
          <span className="text-xs text-gray-400">
            {new Date(review.created_at).toLocaleDateString(undefined, {
              month: 'short', day: 'numeric'
            })}
          </span>
        </div>

        {/* Review Text */}
        <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 mb-4 flex-grow">
          {review.content}
        </p>

        {/* Author Info */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center gap-2">
          {/* Avatar */}
          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600">
            {review.reviewer_name.charAt(0).toUpperCase()}
          </div>
          
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-900 truncate max-w-[120px]">
                {review.reviewer_name}
              </span>
              {settings.show_verified_badge && review.verified_purchase && (
                <BadgeCheck className="w-3 h-3 text-blue-500" fill="currentColor" color="white" />
              )}
            </div>
          </div>
        </div>

        {/* Store Response (Optional) */}
        {settings.show_response && review.response_text && (
          <div className="mt-3 bg-gray-50 rounded p-2 text-xs">
            <span className="font-semibold text-gray-900 block mb-0.5">Response:</span>
            <span className="text-gray-600 line-clamp-2">{review.response_text}</span>
          </div>
        )}
      </div>
    </div>
  );
};
